import Router from 'public/components/Router';
import PublicRoute from 'enums/publicRoutes';
import { Layout } from 'antd';
import { StyledPage } from './style';
import PageContent from './PageContent';
import PageLayout from './PageLayout';
import useQueryApiClient from 'utils/useQueryApiClient';
import DefaultSecondHeader from './DefaultSecondHeader';
import Banner from './Banner';
import { useBannerDispatch, useProductDispatch } from '../../context';
import { Spinner } from 'ui';
import { useCategoryDispatch } from '../../context';
import DefaultFooter from './DefaultFooter/DefaultFooter';
import MobileHeader from '../MobileLayout/MobileHeader/MobileHeader';
import MobileMenu from '../MobileLayout/MobileMenu/MobileMenu';
import CookieConsent from './CookieConsent';
import {
  useCartState,
  CartActionType,
  useCartDispatch,
} from 'public/context/CartContext';
import { useEffect } from 'react';
import useMarketType from '../../../utils/useMarketType';
import { useAuth } from '../../../hooks/useAuth';
import HomeInfoBar from './HomeInfoBar';

const { Content } = Layout;

const DefaultLayout = () => {
  const dispatchBanners = useBannerDispatch();
  const dispatchProducts = useProductDispatch();
  const dispatchCategory = useCategoryDispatch();
  const dispatchCart = useCartDispatch();
  const { marketType } = useMarketType();
  const { marketType: cartMarketType } = useCartState();
  const { user } = useAuth();

  useEffect(() => {
    if (cartMarketType && marketType !== cartMarketType) {
      if (marketType === 'WHOLESALE' && !user) {
        return;
      }

      dispatchCart({
        type: CartActionType.SWITCH_MARKET,
        marketType: marketType as 'EMARKET' | 'WHOLESALE',
      });

      appendData({ type: marketType });
    }
  }, [marketType, cartMarketType]);

  const { appendData } = useQueryApiClient({
    request: {
      url: `api/v2/cart/latest`,
      disableOnMount: true,
    },
    onSuccess: (response) => {
      if (response?.cartId) {
        dispatchCart({
          type: CartActionType.REFETCH_CART_PRODUCTS,
          farmProducts: response.farmProducts,
          priceTotal: response.priceTotal,
          requiresAuth: response.requires_auth,
          cartId: response.cartId,
          cartName: response.cartName,
          marketType: marketType,
        });
      } else {
        dispatchCart({
          type: CartActionType.CLEAR_CART,
        });
      }
    },
  });

  const { refetch: refetchBanners, isLoading: isLoadingBanners } =
    useQueryApiClient({
      request: {
        url: 'api/v2/banners',
      },
      onSuccess: (response) => {
        dispatchBanners({
          type: 'SAVE_PAYLOAD',
          payload: { banners: response, refetch: refetchBanners },
          isLoading: isLoadingBanners,
        });
      },
    });

  const { refetch: refetchProducts, isLoading: isLoadingProducts } =
    useQueryApiClient({
      request: {
        url: 'api/v2/products/popular',
      },
      onSuccess: (response) => {
        dispatchProducts({
          type: 'SAVE_PAYLOAD',
          payload: { products: response, refetch: refetchProducts },
          isLoading: isLoadingProducts,
        });
      },
    });

  const {
    refetch: refetchProductsDiscount,
    isLoading: isLoadingProductsDiscount,
  } = useQueryApiClient({
    request: {
      url: 'api/v2/products/discounted',
    },
    onSuccess: (response) => {
      dispatchProducts({
        type: 'SAVE_PAYLOAD',
        payload: {
          productsDiscounted: response,
          refetch: refetchProductsDiscount,
        },
        isLoading: isLoadingProductsDiscount,
      });
    },
  });

  const { refetch: refetchCategory, isLoading: isLoadingCategory } =
    useQueryApiClient({
      request: {
        url: 'api/v2/categories/menu/desktop',
      },
      onSuccess: (response) => {
        dispatchCategory({
          type: 'SAVE_PAYLOAD',
          payload: { category: response, refetch: refetchCategory },
          isLoading: isLoadingCategory,
        });
      },
    });

  const {
    refetch: refetchPopularCategories,
    isLoading: isLoadingPopularCategories,
  } = useQueryApiClient({
    request: {
      url: 'api/v2/categories/popular',
    },
    onSuccess: (response) => {
      dispatchCategory({
        type: 'SAVE_PAYLOAD',
        payload: {
          popularCategory: response,
          refetch: refetchPopularCategories,
        },
        isLoading: isLoadingPopularCategories,
      });
    },
  });

  const { refetch: refetchProductsCount, isLoading: isLoadingProductsCount } =
    useQueryApiClient({
      request: {
        url: 'api/v2/products/get/count',
      },
      onSuccess: (response) => {
        dispatchProducts({
          type: 'SAVE_PAYLOAD',
          payload: {
            productsCounted: response,
            refetch: refetchProductsCount,
          },
          isLoading: isLoadingProductsCount,
        });
      },
    });

  const { farmProducts } = useCartState();

  const isLoading =
    isLoadingBanners ||
    isLoadingProducts ||
    isLoadingCategory ||
    isLoadingPopularCategories ||
    isLoadingProductsDiscount ||
    isLoadingProductsCount;

  useEffect(() => {
    const cartPaths: string[] = [
      PublicRoute.CART_PAGE,
      PublicRoute.WHOLESALE_CART,
    ];

    if (!cartPaths.includes(window.location.pathname)) {
      dispatchCart({
        type: CartActionType.REMOVE_ALL_COUPONS,
      });
    }
  }, [window.location.pathname]);

  return (
    <>
      <CookieConsent />
      <StyledPage>
        <Spinner spinning={isLoading} size={'large'}>
          <Layout className={'layout'}>
            <MobileHeader />
            <HomeInfoBar />
            <DefaultSecondHeader />
            <Content>
              <Router />
            </Content>
            <DefaultFooter />
            <MobileMenu farmProducts={farmProducts} />
          </Layout>
        </Spinner>
      </StyledPage>
    </>
  );
};

DefaultLayout.PageLayout = PageLayout;
DefaultLayout.PageContent = PageContent;
DefaultLayout.Banner = Banner;

export default DefaultLayout;
